import { Placement as PopoverPlacement } from '@popperjs/core';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { DisableAutoFocus } from '../../utils/AutoFocus';
import { makeTestId } from '../../utils/Declarative';
import { generateId } from '../../utils/DomUtil';
import { ElemAttrs } from '../../utils/HtmlAttrs';
import { StackingComponentProps } from '../../utils/StackingComponent';
import { AlertType as PopoverAlertType } from '../AlertStyles';
import { ButtonWidget } from '../Button/ButtonWidgetShim';
import { PopoverBase } from './PopoverBase';
import { PopoverTriggerStyleName } from './PopoverStyles';

export { PopoverAlertType };
export { PopoverPlacement };

export type PopoverWidth = 200 | 300 | 400;

export type PopoverProps = DisableAutoFocus & Pick<StackingComponentProps, 'portalEventTrapOverrides' | 'stackingRootId' | 'zIndex'> & {
  /**
  * Optionally adds an alert style to the popover.
  * Note: that supplying a value here will always show a title bar, even if `showTitleBar` is set to `false`.
  */
  alertType?: PopoverAlertType;

  /** If your popover contains text content which helps to explain the popover, pass in that element's id to this prop. */
  'aria-describedby'?: string;

  children?: never;

  /**
   * When true, the text and close button colors will be adjusted for a dark background.
   * Don't forget to set a dark background color in the `style` prop!
  */
  darkMode?: boolean;

  /** Will create `ButtonWidget`s in the footer which are aligned and spaced in a standardized way. */
  footerButtons?: Array<ButtonWidget['props']>;

  /**
   * When this prop is omitted, when the popover closes, it attempts to return focus to the element which was focused when the popover was opened.
   * If you would like to customize which element receives focus when the popover closes, return that element via this callback.
   * This may be useful e.g. if the previously-focused element has been removed from the DOM by the time the popover closes.
   */
  getElementToRefocus?: () => HTMLElement;

  /**
   * Where the popover should attempt to position itself, relative to its `triggerElement`.
   *
   *  * top/right/bottom/left: Indicate which side of the `triggerElement` to align to.
   *  * start/end: On vertical sides, start indicates the top and end indicates the bottom.
   *  * start/end: On horizontal sides, start indicates the left and end indicates the right.
   * @default ```'auto'```
   */
  placement?: PopoverPlacement;

  /** The content rendered inside of the popover. */
  popoverContent: React.ReactChild;

  /**
   * Whether or not to display a title bar in the popover.
   * Note: that supplying a value to `alertType` will always show a title bar, regardless of this value.
   */
  showTitleBar?: boolean;

  /** Added to the root `<div>` with the suffix `-PopoverWidget` */
  testId?: string;

  /** A title is required for accessiblity, even if not shown visually. */
  title: React.ReactChild;

  /**
   * The element which triggers the popover, and to which the popover will be anchored.
   * Generally this will be a `ButtonWidget`.
   * `aria-controls` and `aria-haspopup` will be overridden on this element.
   */
  triggerElement: React.ReactElement;

  /**
   * Sets the width of the popover.
   * Per TDLS, you should choose one of the following 3 sizes:
   * * `200`
   * * `300`
   * * `400`
   * @default ```300```
   */
  width?: PopoverWidth;
};

export type PopoverInstance = {
  /**
   * Toggles the popover.
   * @param open optional parameter, ```true``` to open the popover or ```false``` to close it.
   */
  toggle: (open?: boolean) => void
};

export const PopoverWidget = React.forwardRef<PopoverInstance, PopoverProps & Omit<ElemAttrs, keyof PopoverProps>>((props, ref) => {
  const { triggerElement, ...popoverProps } = props;

  const [isVisible, setIsVisible] = React.useState<boolean>(false);
  const triggerElRef = React.useRef<HTMLElement>();
  const [triggerId] = React.useState<string>(triggerElement.props.id || generateId());
  const [defaultPopoverId] = React.useState(generateId());
  const popoverId = props.id || defaultPopoverId;

  React.useImperativeHandle(ref, (): PopoverInstance => ({
    toggle(shown: boolean = !isVisible): void {
      if (shown && !triggerElRef.current) {
        triggerElRef.current = document.getElementById(triggerId) || undefined;
      }
      setIsVisible(shown);
    }
  }));

  const handleClose = () => setIsVisible(false);

  const clonedTrigger = React.cloneElement(triggerElement, {
    'aria-controls': isVisible ? popoverId : undefined,
    'aria-haspopup': 'dialog',
    className: TypeStyle.classes(triggerElement.props.className, PopoverTriggerStyleName),
    'data-tb-test-id': makeTestId(props.testId!, 'PopoverWidget-Trigger'),
    id: triggerId,
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      triggerElRef.current = e.currentTarget;
      setIsVisible(!isVisible);
      triggerElement.props.onClick?.(e);
    }
  });

  return <>
    {clonedTrigger}
    {isVisible && triggerElRef.current &&
      <PopoverBase {...popoverProps} id={popoverId} onClose={handleClose} trigger={triggerElRef.current} />
    }
  </>;
});
PopoverWidget.displayName = 'PopoverWidget';
PopoverWidget.defaultProps = {
  placement: 'auto',
  width: 300,
  zIndex: 1,
};
